import { useState } from 'react';

import { Button, Flex, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconDownload from '@/assets/icons/icon-download.svg?react';
import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import Card from '@/components/RCard';
import RToggleButtons from '@/components/RToggleButtons';
import Tooltip from '@/components/RTooltip';
import { SimulationResult } from '@/types/simulations';

import AncillaryTable from './AncillaryTable';
import EnergyTable from './EnergyTable';
import TotalTable from './TotalTable';

interface IncomeBreakdownProps {
  activeTab?: number;
  simulationId?: number;
}

const IncomeBreakdown = ({ activeTab, simulationId }: IncomeBreakdownProps) => {
  const { t } = useTranslation();
  const params = useParams<Record<string, string>>();
  const resultId = simulationId || params.resultId;
  const {
    data: simulationData,
    isLoading,
    error,
  } = useSWR<SimulationResult>(`/simulation/${resultId}/`, {
    revalidateOnFocus: false,
  });
  const buttons = [
    t('result.finance.incomeBreakdown.toggleButtons.energySales'),
    t('result.finance.incomeBreakdown.toggleButtons.ancillaryServices'),
    t('result.finance.incomeBreakdown.toggleButtons.total'),
  ];
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const theme = useTheme();
  const currentTab = typeof activeTab === 'number' ? activeTab : activeIndex;
  const handleButtonClick = (index: number) => {
    setActiveIndex(index);
  };
  return (
    <Card
      title={
        <Flex gap={8} align="center">
          <Typography.Title level={4} className="fs-17-bold">
            {t('result.finance.incomeBreakdown.title')}
          </Typography.Title>
          <Tooltip
            title={t('result.finance.incomeBreakdown.title')}
            description={t('result.finance.incomeBreakdown.tooltip')}
          >
            <IconInfo />
          </Tooltip>
        </Flex>
      }
      extra={
        <Flex gap={16} align="center">
          <RToggleButtons
            labels={buttons}
            onButtonClick={handleButtonClick}
            initialActiveIndex={currentTab}
          />
          <Button icon={<IconDownload fill={theme.colors.bluePrimary} />} />
        </Flex>
      }
      styles={{ header: { padding: '14px 24px' } }}
      $paddingBody="12px 0"
      style={{ marginTop: '24px' }}
    >
      {isLoading ? (
        <Flex justify="center" align="center" style={{ height: 304 }}>
          <Spin style={{ margin: '20px' }} />
        </Flex>
      ) : error ? (
        <Flex
          vertical
          gap={12}
          justify="center"
          align="center"
          style={{ height: 304 }}
        >
          <IconError width={50} height={50} fill={theme.colors.tagFailure} />
          <Typography.Text className="fs-14-regular text-gray-color">
            {t('result.error.fetchDataError')}
          </Typography.Text>
        </Flex>
      ) : (
        <>
          {' '}
          <Flex
            gap={8}
            justify="center"
            align="center"
            style={{
              padding: '19.5px 24px',
              backgroundColor: theme.colors.bgBlueLightTransparent,
            }}
          >
            <Typography.Text className="fs-14-bold">
              {buttons[currentTab]}
            </Typography.Text>
          </Flex>
          {currentTab === 0 && simulationData && (
            <EnergyTable simulationData={simulationData} />
          )}
          {currentTab === 1 && simulationData && (
            <AncillaryTable simulationData={simulationData} />
          )}
          {currentTab === 2 && simulationData && (
            <TotalTable simulationData={simulationData} />
          )}
        </>
      )}
    </Card>
  );
};
export default IncomeBreakdown;
